import { useLayoutEffect } from 'react';
import { NetworkStatus } from '@apollo/client';
import { size, throttle } from 'lodash';
import { handleFetchMore } from '@src/util/handleFetchMore';
import { CompanyReviewsQuery, useCompanyReviewsQuery } from '@src/graphql/generated';
import { MetricAction, MetricContext } from '@src/metrics/metrics.enum';
import { trackEvent } from '@src/metrics';
import { UseCompanyReviewsValue } from './interfaces/useCompanyReviews.interface';

const FIRST_PATCH_SIZE = 8;
const NEXT_PATCH_SIZE = 10;

export const useCompanyReviews = (): UseCompanyReviewsValue => {
  const { data, networkStatus, fetchMore } = useCompanyReviewsQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    variables: {
      paginationOptions: { limit: FIRST_PATCH_SIZE },
    },
  });

  const pagination = data?.company.reviews.pagination;
  const reviews = data?.company.reviews.result || [];
  const companyRatings = data?.company.ratings;

  const isFetchMoreLoading = networkStatus === NetworkStatus.fetchMore;
  const isInitialLoading = networkStatus <= 2;
  const isLoading = isInitialLoading || isFetchMoreLoading;

  const loadMore = throttle(() => {
    const {
      body: { scrollHeight },
    } = document;
    const { innerHeight, scrollY } = window;
    const distanceFromBottom = scrollHeight - innerHeight - scrollY;

    if (!isLoading && !isFetchMoreLoading && pagination?.hasMoreResults && distanceFromBottom < 100) {
      handleFetchMore(fetchMore, {
        updateQuery: (
          previousResult: CompanyReviewsQuery,
          { fetchMoreResult }: { fetchMoreResult: CompanyReviewsQuery },
        ) => {
          if (!fetchMoreResult) {
            return previousResult;
          }

          const previousCompanyState = previousResult.company;
          const previousReviews = previousResult.company.reviews;

          const newResult = fetchMoreResult.company.reviews.result;
          const pagination = fetchMoreResult.company.reviews.pagination;

          trackEvent({
            context: MetricContext.COMPANY_REVIEWS_VIEW,
            interaction: MetricAction.FETCH_MORE,
            payload: {
              itemType: 'Company reviews feed',
              itemValue: size([...previousReviews.result, ...newResult]),
            },
          });

          return {
            company: {
              ...previousCompanyState,
              reviews: {
                ...previousReviews,
                pagination: {
                  ...previousReviews.pagination,
                  hasMoreResults: pagination.hasMoreResults,
                  limit: pagination.limit,
                  maxCursor: pagination.maxCursor,
                },
                result: [...previousReviews.result, ...newResult],
              },
            },
          };
        },
        variables: {
          paginationOptions: {
            limit: NEXT_PATCH_SIZE,
            minCursor: pagination.maxCursor,
          },
        },
      });
    }
  }, 200);

  useLayoutEffect(() => {
    window.addEventListener('scroll', loadMore);

    return () => {
      window.removeEventListener('scroll', loadMore);
      loadMore.cancel();
    };
  }, [isLoading, pagination]);

  return {
    companyRatings,
    isFetchMoreLoading,
    isInitialLoading,
    loadMore,
    pagination,
    reviews,
  };
};
